import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type {
  IDefaultAPIResult,
  IDefaultReducerState,
} from 'types/state.interface';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import type {
  THoliday,
  THolidayRegion,
  THolidayReservation,
} from '@timeedit/types';
import api from '../services/api.service';

export type THolidayReservationState = IDefaultReducerState & {
  holidayRegions: THolidayRegion[];
};

export const initialState: THolidayReservationState = {
  loading: false,
  hasErrors: false,
  holidayRegions: [],
};

const slice = createSlice({
  name: 'holidays',
  initialState,
  reducers: {
    fetchHolidayRegionsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchHolidayRegionsSuccess: (
      state,
      { payload }: PayloadAction<IDefaultAPIResult<THolidayRegion>>
    ) => {
      state.holidayRegions = payload.results;
      finishedLoadingSuccess(state);
    },
    fetchHolidayRegionsFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
});

export default slice.reducer;

// Selectors
export const holidayRegionsSelector = (state: TRootState) =>
  state.holidayReservations.holidayRegions as THolidayRegion[];

// Actions
export const {
  fetchHolidayRegionsRequest,
  fetchHolidayRegionsSuccess,
  fetchHolidayRegionsFailure,
} = slice.actions;

export const fetchHolidayRegions = createAppAsyncThunk(
  'holidays/fetchHolidayRegions',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchHolidayRegionsRequest());
      const holidayRegions = (await api.get({
        endpoint: `/holidays/regions`,
      })) as IDefaultAPIResult<THolidayRegion>;
      dispatch(fetchHolidayRegionsSuccess(holidayRegions));
    } catch (e) {
      dispatch(fetchHolidayRegionsFailure());
      return console.error(e);
    }
  }
);

export const getListOfHolidays = async (
  holidayRegion: string,
  holidayType: string,
  startDate: number,
  endDate: number
) => {
  const holidays = (await api.get({
    endpoint: `/holidays?holidayRegion=${holidayRegion}&holidayType=${holidayType}&startDate=${startDate}&endDate=${endDate}`,
  })) as IDefaultAPIResult<THoliday>;
  return holidays.results;
};

type THolidayReservationResponse = {
  numberOfHolidaysCreated: number;
};

export const createHolidayReservation = async (
  holidayReservation: THolidayReservation
) => {
  const { ...holidayReservationBody } = holidayReservation;
  return (await api.post({
    endpoint: `/holidays/reservations`,
    data: holidayReservationBody,
  })) as THolidayReservationResponse;
};
